import * as React from 'react';
import * as S from './styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/types';
import { getLMKeywordsAPI, postLMKeywordsAPI } from './state/actions';
import { SectionWrapper } from 'common/baseComponents';
import { ReactComponent as IconKeywords } from 'common/images/icon-keywords.svg';
import InputIconField, { InputIconFieldAction, InputIconFieldState } from 'components/InputIcon';
import { serviceLoading } from 'common/loaderReducer';
import { GET_LMKEYWORDS_API_REQUEST, POST_LMKEYWORDS_API_REQUEST } from 'features/Dealers/Listings/Keywords/state/actionTypes';
import ViewKeywords from 'features/Dealers/Listings/Keywords/ViewKeywords';
import EmptyKeywords from 'features/Dealers/Listings/Keywords/EmptyKeywords';
import EditKeywords from 'features/Dealers/Listings/Keywords/EditKeywords';
import Loader from 'components/Loader';
import { getListingSyncErrors } from 'features/Dealers/Listings/state/actions';
import { getDealerListingsWithErrors } from 'features/Dealers/Listings/state/actions';
import { getSyncError } from 'features/Dealers/Listings/utils';
import * as _ from 'lodash';
import { Keyword } from 'features/Dealers/Listings/Keywords/state/types';

export interface KeywordFormat extends Keyword {
  editing?: boolean;
}

const Keywords: React.FC = () => {
  const dispatch = useDispatch();
  const [firstLoad, setFirstLoad] = React.useState(true)

  const [editingState, setEditingState] = React.useState(InputIconFieldState.VIEW);
  const [keywordsState, setKeywordsState] = React.useState<KeywordFormat[]>([]);

  const {
    isLoading,
    keywordsData,
    isSaving,
    dealerId,
    listing,
    listingSyncErrors,
    selectedDealerId,
    selectedListingId,
  } = useSelector((rootState: RootState) => {
    return {
      isLoading: serviceLoading(rootState, [GET_LMKEYWORDS_API_REQUEST]),
      isSaving: serviceLoading(rootState, [POST_LMKEYWORDS_API_REQUEST]),
      keywordsData: rootState.dealers.listing.currentListing.keywordsData.keywordsData,
      dealerId: rootState.dealers.dealer.selectedDealer?.id,
      listing: rootState.dealers.listing,
      listingSyncErrors: rootState.dealers.listing.listings.listingSyncErrors,
      selectedDealerId: rootState.dealers.dealer.selectedDealer?.id,
      selectedListingId: rootState.dealers.listing.listings.selectedListingId,
    }
  }, shallowEqual);

  const setKeywordsFromSelector = () => {
    if (keywordsData?.keywords?.length) setKeywordsState(_.cloneDeep(keywordsData.keywords));
    else setKeywordsState([]);
  }

  React.useEffect(() => {
    if (selectedListingId && selectedListingId != "") {
      dispatch(getLMKeywordsAPI(selectedListingId));
    }
  }, [selectedListingId]);

  React.useEffect(() => {
    setKeywordsFromSelector();
  }, [JSON.stringify(keywordsData?.keywords)]);

  React.useEffect(() => {
    if (!firstLoad && !isSaving && selectedListingId && selectedListingId != "" && selectedDealerId) {
      dispatch(getListingSyncErrors(selectedListingId ?? ""));
      dispatch(getDealerListingsWithErrors(selectedDealerId ?? ""));
    }
    setFirstLoad(false);
  }, [isSaving])

  if (isLoading || !selectedListingId || selectedListingId == "") {
    return <Loader />;
  }

  const onFieldActionClick = (action: InputIconFieldAction) => {
    switch (action) {
      case InputIconFieldAction.SAVE:
      {
        setEditingState(InputIconFieldState.VIEW);
        let newKeywordsState = [...keywordsState];
        newKeywordsState.map(keyword => {delete keyword.editing});
        if (selectedListingId && selectedListingId != "") {
          dispatch(postLMKeywordsAPI(selectedListingId, { keywords: [...newKeywordsState] }));
        }
        break;
      }
      case InputIconFieldAction.EDIT:
        {
          setEditingState(InputIconFieldState.EDIT);
          break;
        }
      case InputIconFieldAction.CANCEL:
        {
          setEditingState(InputIconFieldState.VIEW);
          setKeywordsFromSelector();
        }
    }
  }

  const renderHelpText = () => {
    return (
      <S.HelpText>
        Your location’s rankings will be monitored when people search for these terms
      </S.HelpText>
    )
  }

  const renderKeywords = () => {
    if (editingState === InputIconFieldState.VIEW || editingState === InputIconFieldState.LOADING) {
      if (keywordsData?.keywords?.length) {
        return (
          <>
            {renderHelpText()}
            <ViewKeywords
              keywordsState={keywordsState}
            />
          </>
        )
      } else {
        return <EmptyKeywords />
      }
    } else if (editingState === InputIconFieldState.EDIT) {
      return (
        <>
          {renderHelpText()}
          <EditKeywords
            keywordsState={keywordsState}
            setKeywordsState={setKeywordsState}
          />
        </>
      )
    }
  }

  return (
    <S.GeneralContainer>
        <SectionWrapper>
          <InputIconField
            label={'Keywords'}
            state={isSaving ? InputIconFieldState.LOADING : editingState}
            renderValue={renderKeywords}
            renderIcon={() => { return (<S.IconContainer><IconKeywords /></S.IconContainer>) }}
            showAlertIcon={getSyncError('keywords', listingSyncErrors) ? true : false}
            alertToolTip={getSyncError('keywords', listingSyncErrors)}
            showEditErrorMessage={getSyncError('keywords', listingSyncErrors) ? true : false}
            editErrorMessage={getSyncError('keywords', listingSyncErrors)}
            onActionClick={(action: InputIconFieldAction) => { onFieldActionClick(action) }}
            disabled={false}
            disableBackground={true}
          />
        </SectionWrapper>
    </S.GeneralContainer>
  )
}

export default Keywords;

